<!--
  AscendQE: team leave timeline (Gantt-style) for Leave > Leave List.
  Rows are employees, columns are the days of the selected month; each leave
  request is a bar coloured by status. Uses the same leave-requests endpoint
  and filters as the list view, scoped to the visible month.
-->
<template>
  <div class="aqe-tl">
    <div class="aqe-tl__bar">
      <div class="aqe-tl__nav">
        <button
          type="button"
          class="aqe-tl__btn"
          aria-label="Previous month"
          @click="shift(-1)"
        >
          <i class="oxd-icon bi-chevron-left"></i>
        </button>
        <h3 class="aqe-tl__title" aria-live="polite">{{ monthTitle }}</h3>
        <button
          type="button"
          class="aqe-tl__btn"
          aria-label="Next month"
          @click="shift(1)"
        >
          <i class="oxd-icon bi-chevron-right"></i>
        </button>
        <button type="button" class="aqe-tl__today" @click="goToday">
          Today
        </button>
      </div>
      <div class="aqe-tl__legend">
        <span v-for="s in legend" :key="s.key"
          ><i :data-s="s.key"></i>{{ s.label }}</span
        >
      </div>
    </div>

    <div
      ref="scroller"
      class="aqe-tl__scroll"
      tabindex="0"
      aria-label="Leave timeline, scroll horizontally"
    >
      <div class="aqe-tl__grid" :style="{'--days': days.length}">
        <!-- header -->
        <div class="aqe-tl__corner">Employee</div>
        <div
          v-for="d in days"
          :key="'h' + d.iso"
          class="aqe-tl__day"
          :class="{
            'is-weekend': d.weekend,
            'is-today': d.today,
            'is-holiday': !!holidays[d.iso],
          }"
          :title="holidays[d.iso] || undefined"
        >
          <span>{{ d.dow }}</span
          ><b>{{ d.n }}</b>
        </div>

        <!-- rows -->
        <template v-if="loading && !rows.length">
          <div
            v-for="n in 5"
            :key="'sk' + n"
            class="aqe-tl__row aqe-tl__row--skel"
          >
            <div class="aqe-tl__who"><span></span></div>
            <div class="aqe-tl__lane"></div>
          </div>
        </template>
        <div v-else-if="!rows.length" class="aqe-tl__empty">
          No leave in {{ monthTitle }} for these filters.
        </div>
        <div v-for="r in rows" :key="r.empNumber" class="aqe-tl__row">
          <div class="aqe-tl__who">
            <span class="aqe-tl__avatar">{{ r.initials }}</span>
            <span class="aqe-tl__name">{{ r.name }}</span>
            <span class="aqe-tl__days">{{ r.total }}d</span>
          </div>
          <div class="aqe-tl__lane">
            <div
              v-for="d in days"
              :key="'c' + d.iso"
              class="aqe-tl__cell"
              :class="{
                'is-weekend': d.weekend,
                'is-today': d.today,
                'is-holiday': !!holidays[d.iso],
              }"
              :style="{gridColumn: d.n}"
            ></div>
            <a
              v-for="b in r.bars"
              :key="b.id"
              class="aqe-tl__leave"
              :data-s="b.status"
              :href="b.href"
              :style="{gridColumn: `${b.start} / span ${b.span}`}"
              :aria-label="`${r.name}: ${b.type}, ${b.range}, ${b.statusLabel}`"
              @mouseenter="
                tip = {...b, who: r.name, x: $event.clientX, y: $event.clientY}
              "
              @mousemove="
                tip && (tip = {...tip, x: $event.clientX, y: $event.clientY})
              "
              @mouseleave="tip = null"
              @focus="tipFromFocus($event, b, r)"
              @blur="tip = null"
            >
              <span>{{ b.type }}</span>
            </a>
          </div>
        </div>
      </div>
    </div>

    <teleport to="body">
      <div
        v-if="tip"
        class="aqe-tl-tip"
        role="tooltip"
        :style="{left: tip.x + 14 + 'px', top: tip.y + 14 + 'px'}"
      >
        <strong>{{ tip.who }}</strong>
        <span>{{ tip.type }} · {{ tip.statusLabel }}</span>
        <span>{{ tip.range }}</span>
      </div>
    </teleport>
  </div>
</template>

<script>
import {APIService} from '@/core/util/services/api.service';

const pad = (n) => String(n).padStart(2, '0');
const iso = (d) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const parse = (s) => {
  const [y, m, d] = String(s).slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, d);
};

export default {
  name: 'LeaveTimeline',
  props: {
    query: {type: Object, default: () => ({})},
    myLeaveList: {type: Boolean, default: false},
  },

  setup(props) {
    const http = new APIService(
      window.appGlobal.baseUrl,
      `/api/v2/leave/${
        props.myLeaveList ? 'leave-requests' : 'employees/leave-requests'
      }`,
    );
    return {http};
  },

  data() {
    const t = new Date();
    return {
      year: t.getFullYear(),
      month: t.getMonth(),
      items: [],
      holidays: {},
      loading: false,
      tip: null,
      seq: 0,
      legend: [
        {key: 'scheduled', label: 'Scheduled'},
        {key: 'pending', label: 'Pending'},
        {key: 'taken', label: 'Taken'},
        {key: 'other', label: 'Rejected / Cancelled'},
      ],
    };
  },

  computed: {
    monthTitle() {
      return new Date(this.year, this.month, 1).toLocaleString(undefined, {
        month: 'long',
        year: 'numeric',
      });
    },
    days() {
      const n = new Date(this.year, this.month + 1, 0).getDate();
      const today = iso(new Date());
      return Array.from({length: n}, (_, i) => {
        const d = new Date(this.year, this.month, i + 1);
        return {
          n: i + 1,
          iso: iso(d),
          dow: d.toLocaleString(undefined, {weekday: 'narrow'}),
          weekend: d.getDay() === 0 || d.getDay() === 6,
          today: iso(d) === today,
        };
      });
    },
    rows() {
      const first = new Date(this.year, this.month, 1);
      const last = new Date(this.year, this.month + 1, 0);
      const by = new Map();
      this.items.forEach((it) => {
        const emp = it.employee || {};
        const key = emp.empNumber ?? 'me';
        if (!by.has(key)) {
          const name =
            [emp.firstName, emp.lastName].filter(Boolean).join(' ') || 'Me';
          by.set(key, {
            empNumber: key,
            name,
            initials: name
              .split(' ')
              .map((p) => p[0])
              .join('')
              .slice(0, 2)
              .toUpperCase(),
            bars: [],
            total: 0,
          });
        }
        const from = parse(it.dates?.fromDate);
        const to = it.dates?.toDate ? parse(it.dates.toDate) : from;
        const s = from < first ? first : from;
        const e = to > last ? last : to;
        if (e < first || s > last) return;
        const status = this.statusKey(it);
        const row = by.get(key);
        row.total += Number(it.noOfDays || 0);
        row.bars.push({
          id: it.id,
          start: s.getDate(),
          span: e.getDate() - s.getDate() + 1,
          type: it.leaveType?.name || 'Leave',
          status,
          statusLabel:
            (it.leaveBreakdown || []).map((b) => b.name).join(', ') || status,
          range:
            from.getTime() === to.getTime()
              ? from.toLocaleDateString()
              : `${from.toLocaleDateString()} – ${to.toLocaleDateString()}`,
          href: `${window.appGlobal.baseUrl}/leave/viewLeaveRequest/${it.id}`,
        });
      });
      return [...by.values()]
        .map((r) => ({...r, total: Math.round(r.total * 10) / 10}))
        .sort((a, b) => a.name.localeCompare(b.name));
    },
  },

  watch: {
    query: {deep: true, handler: 'load'},
  },

  mounted() {
    this.load();
  },

  methods: {
    statusKey(it) {
      const names = (it.leaveBreakdown || [])
        .map((b) => String(b.name).toLowerCase())
        .join(' ');
      if (names.includes('pending')) return 'pending';
      if (names.includes('scheduled')) return 'scheduled';
      if (names.includes('taken')) return 'taken';
      return 'other';
    },
    shift(n) {
      const d = new Date(this.year, this.month + n, 1);
      this.year = d.getFullYear();
      this.month = d.getMonth();
      this.load();
    },
    goToday() {
      const t = new Date();
      this.year = t.getFullYear();
      this.month = t.getMonth();
      this.load().then(() => {
        const el = this.$refs.scroller?.querySelector('.aqe-tl__day.is-today');
        el?.scrollIntoView({
          inline: 'center',
          block: 'nearest',
          behavior: 'smooth',
        });
      });
    },
    tipFromFocus(e, b, r) {
      const rect = e.target.getBoundingClientRect();
      this.tip = {...b, who: r.name, x: rect.left, y: rect.bottom};
    },
    async load() {
      const seq = ++this.seq;
      const fromDate = iso(new Date(this.year, this.month, 1));
      const toDate = iso(new Date(this.year, this.month + 1, 0));
      this.loading = true;
      try {
        const [{data}, hol] = await Promise.all([
          this.http.getAll({
            ...this.query,
            fromDate,
            toDate,
            limit: 0,
            offset: 0,
          }),
          this.http
            .request({
              method: 'GET',
              url: '/api/v2/leave/holidays',
              baseURL: window.appGlobal.baseUrl,
              params: {fromDate, toDate},
            })
            .catch(() => ({data: {data: []}})),
        ]);
        if (seq !== this.seq) return;
        this.items = data.data || [];
        this.holidays = Object.fromEntries(
          (hol.data?.data || []).map((h) => [h.date, h.name]),
        );
      } finally {
        if (seq === this.seq) this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
$ink: #0f172a;
$ink3: #64748b;
$ink4: #94a3b8;
$line: rgba(15, 23, 42, 0.07);
$col: 34px;
$who: 220px;

.aqe-tl {
  padding: 4px 24px 24px;
  font-family: Inter, 'Nunito Sans', sans-serif;
}
.aqe-tl__bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 12px;
}
.aqe-tl__nav {
  display: flex;
  align-items: center;
  gap: 6px;
}
.aqe-tl__title {
  min-width: 150px;
  margin: 0 4px;
  color: $ink;
  font: 700 16px Inter, sans-serif;
  text-align: center;
}
.aqe-tl__btn,
.aqe-tl__today {
  height: 32px;
  border: 0;
  border-radius: 9px;
  background: #fff;
  box-shadow: 0 0 0 1px rgba(15, 23, 42, 0.1);
  color: #334155;
  cursor: pointer;
  &:hover {
    background: #f8fafc;
  }
}
.aqe-tl__btn {
  width: 32px;
}
.aqe-tl__today {
  padding: 0 12px;
  font: 600 13px Inter, sans-serif;
}
.aqe-tl__legend {
  display: flex;
  gap: 14px;
  flex-wrap: wrap;
  color: $ink3;
  font-size: 12.5px;
  span {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
  i {
    width: 10px;
    height: 10px;
    border-radius: 3px;
  }
}
[data-s='scheduled'] {
  --c: #6366f1;
  --bg: #e0e7ff;
  --fg: #3730a3;
}
[data-s='pending'] {
  --c: #f59e0b;
  --bg: #fef3c7;
  --fg: #92400e;
}
[data-s='taken'] {
  --c: #10b981;
  --bg: #d1fae5;
  --fg: #065f46;
}
[data-s='other'] {
  --c: #94a3b8;
  --bg: #f1f5f9;
  --fg: #475569;
}
.aqe-tl__legend i {
  background: var(--c);
}

.aqe-tl__scroll {
  max-height: 62vh;
  overflow: auto;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 0 0 1px $line;
  &:focus-visible {
    outline: 2px solid #6366f1;
  }
}
.aqe-tl__grid {
  display: grid;
  grid-template-columns: $who repeat(var(--days), $col);
  min-width: max-content;
}
.aqe-tl__corner,
.aqe-tl__day {
  position: sticky;
  top: 0;
  z-index: 2;
  height: 48px;
  background: rgba(248, 250, 252, 0.97);
  border-bottom: 1px solid $line;
}
.aqe-tl__corner {
  left: 0;
  z-index: 3;
  display: flex;
  align-items: center;
  padding: 0 14px;
  color: $ink3;
  font: 600 11.5px Inter, sans-serif;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-right: 1px solid $line;
}
.aqe-tl__day {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: $ink3;
  font-size: 10.5px;
  b {
    color: $ink;
    font-size: 12.5px;
    font-weight: 600;
  }
  &.is-weekend {
    color: $ink4;
    b {
      color: $ink4;
    }
  }
  &.is-holiday b {
    color: #b45309;
  }
  &.is-today b {
    display: grid;
    place-items: center;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: $ink;
    color: #fff;
  }
}
.aqe-tl__row {
  display: contents;
}
.aqe-tl__who {
  position: sticky;
  left: 0;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 10px;
  height: 46px;
  padding: 0 14px;
  background: #fff;
  border-bottom: 1px solid $line;
  border-right: 1px solid $line;
}
.aqe-tl__avatar {
  flex: 0 0 26px;
  height: 26px;
  display: grid;
  place-items: center;
  border-radius: 50%;
  background: #e0e7ff;
  color: #3730a3;
  font: 700 10.5px Inter, sans-serif;
}
.aqe-tl__name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: $ink;
  font: 600 13px Inter, sans-serif;
}
.aqe-tl__days {
  color: $ink4;
  font-size: 12px;
}
.aqe-tl__lane {
  grid-column: 2 / -1;
  display: grid;
  grid-template-columns: repeat(var(--days), $col);
  grid-template-rows: 46px;
  position: relative;
  border-bottom: 1px solid $line;
}
.aqe-tl__cell {
  grid-row: 1;
  border-right: 1px solid rgba(15, 23, 42, 0.035);
  &.is-weekend {
    background: repeating-linear-gradient(
      135deg,
      #f8fafc 0 6px,
      #f1f5f9 6px 7px
    );
  }
  &.is-holiday {
    background: rgba(245, 158, 11, 0.08);
  }
  &.is-today {
    box-shadow: inset 2px 0 0 #6366f1;
  }
}
.aqe-tl__leave {
  grid-row: 1;
  z-index: 1;
  align-self: center;
  height: 28px;
  margin: 0 2px;
  display: flex;
  align-items: center;
  padding: 0 8px;
  border-radius: 8px;
  background: var(--bg);
  box-shadow: inset 3px 0 0 var(--c);
  color: var(--fg);
  font: 600 11.5px Inter, sans-serif;
  text-decoration: none;
  overflow: hidden;
  white-space: nowrap;
  transition: transform 0.12s, box-shadow 0.12s;
  span {
    overflow: hidden;
    text-overflow: ellipsis;
  }
  &:hover,
  &:focus-visible {
    outline: none;
    transform: translateY(-1px);
    box-shadow: inset 3px 0 0 var(--c), 0 6px 14px -6px rgba(15, 23, 42, 0.35);
  }
}
.aqe-tl__empty {
  grid-column: 1 / -1;
  padding: 48px;
  color: $ink4;
  text-align: center;
}
.aqe-tl__row--skel .aqe-tl__who span {
  width: 60%;
  height: 10px;
  border-radius: 5px;
  background: #eef1f5;
}
</style>

<style lang="scss">
.aqe-tl-tip {
  position: fixed;
  z-index: 2000;
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 10px 12px;
  border-radius: 12px;
  background: #0f172a;
  color: #e2e8f0;
  font: 500 12.5px Inter, 'Nunito Sans', sans-serif;
  box-shadow: 0 16px 32px -12px rgba(15, 23, 42, 0.5);
  pointer-events: none;
  strong {
    color: #fff;
    font-size: 13px;
  }
}
</style>
